import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output } from "@angular/core";
import { syncButtonNameFromRow } from "directives/cs-capture-cover/row-name-source";

@Component({
    selector: 'cs-toggle',
    templateUrl: 'cs-toggle.html',
    host: { 'class': 'toggle' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CsToggleComponent {

    @Input() checked: boolean | null;
    @Output() checkedChange: EventEmitter<boolean | null> = new EventEmitter<boolean | null>();
    @Input() disabled: boolean = false;

    /**
     * What a screen reader announces for this toggle, when the row it sits in is not the right
     * name.
     *
     * The toggle draws a track and a thumb and no text at all, so the words that say what it
     * switches are always outside the component. Without this input the inner `<button>` takes
     * its name from the enclosing row's text through `aria-labelledby` (see
     * `syncButtonNameFromRow`). Give it a name here when the toggle has no such row.
     */
    @Input() ariaLabel?: string;

    constructor(public elementRef: ElementRef) {

    }

    ngAfterViewChecked() {
        // the inner <button> is empty, so without an ariaLabel it takes its name from the row's text
        syncButtonNameFromRow(this.elementRef, this.ariaLabel);
    }

    ngOnChanges(changesSimple: any) {
        let element = this.elementRef.nativeElement as HTMLElement;
        if (changesSimple.checked) {
            // anything that is not an explicit yes clears the checked look. `null`
            // used to leave it in place, so a toggle going from on to neutral kept
            // the accent colour while its thumb moved to the middle (#5712)
            if (this.checked === true) {
                element.classList.add('toggle-checked');
            } else {
                element.classList.remove('toggle-checked');
            }
            if (this.checked === null) {
                element.classList.add('toggle-neutral');
            } else {
                element.classList.remove('toggle-neutral');
            }
        }
        if (changesSimple.disabled) {
            if (this.disabled) {
                element.classList.add('toggle-disabled');
            } else {
                element.classList.remove('toggle-disabled');
            }
        }

    }

    handleClick(event: Event) {
        event.stopPropagation();
        if (!this.disabled) {
            this.checked = !this.checked;
            this.checkedChange.emit(this.checked);
        }
    }
}